import { useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { requiresSecondFactor, type Role } from '@mahallu/shared';
import { ApiError, auth, useMe } from '../api';
import { SignOutButton } from './SignOutButton';

/**
 * Gate for the committee screens: signed in, second factor done, a mahallu chosen, acting in a
 * committee role (every role but MEMBER). What each role may see is then decided by the API.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [error, setError] = useState('');

  if (me.isPending) return <div className="auth-wrap"><p className="note">{t('table.loading')}</p></div>;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
    return <div className="auth-wrap"><p className="form-error" role="alert">{t('signin.error')}</p></div>;
  }
  const data = me.data;
  if (data.mfa.state === 'PENDING' || !data.mahallu) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  if (!data.role || !requiresSecondFactor(data.role)) {
    // A member who also holds a committee role can move to it from here.
    const committee = data.roles.filter((r) => requiresSecondFactor(r));
    const switchTo = async (role: Role) => {
      setError('');
      try {
        const step = await auth.switchRole(role);
        qc.clear();
        navigate(step.status === 'MFA_REQUIRED' ? '/sign-in' : '/', { replace: true });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : t('signin.error'));
      }
    };
    return (
      <div className="auth-wrap">
        <main className="auth-card auth-form">
          <h1>{t('member.title')}</h1>
          <p className="note">{t('member.body')}</p>
          {committee.map((r) => (
            <button key={r} type="button" className="btn primary" onClick={() => void switchTo(r)}>
              {t('member.continueAs', { role: t(`role.${r}`) })}
            </button>
          ))}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <SignOutButton className="btn" />
        </main>
      </div>
    );
  }
  return <>{children}</>;
}
